import { Component, OnInit } from '@angular/core';
import { PagesStore } from 'src/app/core/utils/pages-store/pages-store';
import { TicketHeader } from '../models/ticket-header';
import { mspTicketsSortColumns } from '../interfaces/msp-ticket-sort-columns';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import {
  ApiPagesStoreRequestData,
  PagesStoreRequestData,
} from 'src/app/core/utils/pages-store/pages-store-request-data';
import { PagesStoreResponse } from 'src/app/core/utils/pages-store/pages-store-response';
import { FormControl, FormGroup } from '@angular/forms';
import { ITicketFiltersForm } from '../interfaces/tickets-filter-form';
import { IFormControlConfig } from 'src/app/features/reactive-forms-ui/interfaces/form-control-config';
import { OneLineTextControlConfig } from 'src/app/features/reactive-forms-ui/models/one-line-text-control-config';
import { FiltersPanelLayoutConfig } from 'src/app/features/reactive-forms-ui/models/filters-panel-layout-config';
import { CheckboxTreeControlConfig } from 'src/app/features/reactive-forms-ui/models/checkbox-tree-control-config';
import { ManagedServicesRepository } from '../../services/managed-services-repository.service';
import { DatetimeRangeQuickPickerControlConfig } from 'src/app/features/reactive-forms-ui/models/datetime-range-quick-picker-control-config';
import { SegmentedControlConfig } from 'src/app/features/reactive-forms-ui/models/segmented-control-config';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, distinctUntilChanged, map, Subscription } from 'rxjs';
import { ManagedServicesSharedDataService } from '../../services/managed-services-shared-data.service';
import { PermissionsService } from 'src/app/core/services/permissions.service';
import { FeaturesFlagsV2Service } from 'src/app/core/services/features-flags-v2.service';
import { IMspBoard } from '../interfaces/msp-board';
import { ICheckboxTreeSelectedItems } from 'src/app/features/reactive-forms-ui/control-components/checkbox-tree-control/interfaces/checkbox-tree-selected-item';
import { normalizeBusinessUnitName } from 'src/app/shared/utils/cw-format';
import { UserDataService } from 'src/app/core/services/user-data.service';

@Component({
  selector: 'app-tickets-table-v2',
  templateUrl: './tickets-table-v2.component.html',
  styleUrls: ['./tickets-table-v2.component.scss'],
  standalone: false,
})
export class TicketsTableV2Component implements OnInit {
  store: PagesStore<TicketHeader>;

  /** The number of tickets to be displayed */
  numberOfTickets: number = 0;

  /** The index of the currently selected page */
  pageIndex: number = 0;

  /** The direction by which to sort the data */
  private sortDirection: string = 'DESC';

  /** The column by which to sort the tickets */
  private sortColumn: string = mspTicketsSortColumns.lastUpdated;

  /** The number of pages to simultaneously fetch from the DB */
  readonly pagesPerRequest: number = 1;

  /** The size of the pages for the table */
  readonly pageSize: number = 10;

  // store today's date
  today: Date = new Date();

  isBusinessUnitFlagEnabled = false;

  canSeeSitesFilter = false;

  canSeeCategoryFilter = false;

  mspBoards: IMspBoard[] = [];

  categoryItems = [];

  /** Raw sites list from the API — kept so we can re-filter when the selected Business Unit changes. Null until first load. */
  private companySites: { id: number; name: string; businessUnitName: string | null }[] | null = null;

  /** The columns to be displayed in the table */
  get tableColumns(): string[] {
    const columns = [
      'id',
      'statusName',
      'summary',
      'priorityName',
      'relationship',
      'dateEntered',
      'lastUpdated',
      'closedDate',
      'boardName',
      'siteName',
      ...(this.isBusinessUnitFlagEnabled ? ['businessUnitName'] : []),
      'contactFullName',
    ];
    return columns;
  }

  /** The data source for the table */
  dataSource: TicketHeader[] = [];

  filtersForm: FormGroup = new FormGroup({
    ticketNumber: new FormControl(''),
    statusesGroup: new FormControl(['open']),
    summary: new FormControl(''),
    businessUnitName: new FormControl(null),
    priorities: new FormControl(null),
    relationship: new FormControl(['parent']),
    creation: new FormControl(null),
    updated: new FormControl(null),
    closed: new FormControl(null),
    category: new FormControl(null),
    sites: new FormControl(null),
    contact: new FormControl(''),
  });

  valuesMap: ITicketFiltersForm = this.filtersForm.value;

  filtersConfigMap: Map<string, IFormControlConfig> = new Map<string, IFormControlConfig>([
    [
      'ticketNumber',
      new OneLineTextControlConfig({
        label: 'Ticket Number',
        reviewLabel: 'Ticket Number',
        placeholder: 'Enter ticket number',
        showCancelIcon: true,
        trailingIcon: 'icon_search_gray',
      }),
    ],
    [
      'statusesGroup',
      new SegmentedControlConfig({
        collapsibleTitle: 'Statuses',
        reviewLabel: 'Statuses',
        options: [
          { name: 'Open', value: 'open', icon: 'visibility_off', selectedIcon: 'visibility' },
          { name: 'Waiting', value: 'waiting', icon: 'visibility_off', selectedIcon: 'visibility' },
          { name: 'Closed', value: 'closed', icon: 'visibility_off', selectedIcon: 'visibility' },
        ],
        multiSelect: true,
        growAuto: true,
      }),
    ],
    [
      'summary',
      new OneLineTextControlConfig({
        collapsibleTitle: 'Summary',
        reviewLabel: 'Summary',
        placeholder: 'Search Summary',
        showCancelIcon: true,
        trailingIcon: 'icon_search_gray',
      }),
    ],
    [
      'businessUnitName',
      new CheckboxTreeControlConfig({
        collapsibleTitle: 'Business Unit',
        searchPlaceholder: 'Filter Business Units',
        showSearchBar: true,
        classifications: ['Business Units'],
        items: [],
      }),
    ],
    [
      'priorities',
      new CheckboxTreeControlConfig({
        collapsibleTitle: 'Priorities',
        searchPlaceholder: 'Filter Priorities',
        showSearchBar: true,
        classifications: ['Priorities'],
        items: [],
      }),
    ],
    [
      'relationship',
      new SegmentedControlConfig({
        collapsibleTitle: 'Ticket Relation',
        reviewLabel: 'Ticket Relation',
        options: [
          { name: 'Parent', value: 'parent', icon: 'visibility_off', selectedIcon: 'visibility' },
          { name: 'Child', value: 'child', icon: 'visibility_off', selectedIcon: 'visibility' },
        ],
        multiSelect: true,
        growAuto: true,
      }),
    ],
    [
      'creation',
      new DatetimeRangeQuickPickerControlConfig({
        collapsibleTitle: 'Creation',
        reviewLabel: 'Creation',
        startDateTimeLabel: 'Start date',
        endDateTimeLabel: 'End date',
        showTimeInputs: false,
        showCancelButton: true,
      }),
    ],
    [
      'updated',
      new DatetimeRangeQuickPickerControlConfig({
        collapsibleTitle: 'Updated',
        reviewLabel: 'Updated',
        startDateTimeLabel: 'Start date',
        endDateTimeLabel: 'End date',
        showTimeInputs: false,
        showCancelButton: true,
      }),
    ],
    [
      'closed',
      new DatetimeRangeQuickPickerControlConfig({
        collapsibleTitle: 'Closed',
        reviewLabel: 'Closed',
        startDateTimeLabel: 'Start date',
        endDateTimeLabel: 'End date',
        showTimeInputs: false,
        showCancelButton: true,
      }),
    ],
    [
      'category',
      new CheckboxTreeControlConfig({
        collapsibleTitle: 'Board',
        showSelectAll: true,
        classifications: ['Category'],
        shouldAddParent: true,
        items: [],
      }),
    ],
    [
      'sites',
      new CheckboxTreeControlConfig({
        collapsibleTitle: 'Sites',
        showSelectAll: true,
        showSearchBar: true,
        searchPlaceholder: 'Filter Sites',
        classifications: ['Sites'],
        shouldAddParent: true,
        items: [],
      }),
    ],
    [
      'contact',
      new OneLineTextControlConfig({
        collapsibleTitle: 'Contact',
        reviewLabel: 'Contact',
        placeholder: 'Search Contact',
        showCancelIcon: true,
        trailingIcon: 'icon_search_gray',
      }),
    ],
  ]);

  filtersLayoutConfig: FiltersPanelLayoutConfig;

  /** Flag to indicate if the tickets are being fetched */
  get isFetchingTickets(): boolean {
    return this.store.isFetchingData;
  }

  /** Subscriptions collection for cleanup */
  private subscriptions = new Subscription();

  constructor(
    private http: HttpClient,
    private managedServiceRepository: ManagedServicesRepository,
    private route: ActivatedRoute,
    private managedServicesSharedDataService: ManagedServicesSharedDataService,
    private permissionsService: PermissionsService,
    private featuresFlagsV2Service: FeaturesFlagsV2Service,
    private userDataService: UserDataService,
  ) {
    this.store = new PagesStore(
      `${environment.restUrl}/v3/tickets`,
      'GET',
      this.http,
      5,
      this.mapHeaders,
      this.pagesPerRequest,
      undefined,
      this.mapPropertiesFn,
    );
    this.subscriptions.add(
      this.featuresFlagsV2Service.featuresFlags$.subscribe((flags) => {
        this.isBusinessUnitFlagEnabled = flags.some((f) => f.feature === 'business-unit' && f.isActive);
      }),
    );
    const viewAllPermission = this.permissionsService.hasPermission('managed-services', 'Tickets', 'view-all');
    const viewBusinessUnitPermission = this.permissionsService.hasPermission(
      'managed-services',
      'Tickets',
      'view-business-unit',
    );
    const viewSitePermission = this.permissionsService.hasPermission('managed-services', 'Tickets', 'view-site');
    this.canSeeSitesFilter = viewAllPermission || viewBusinessUnitPermission || viewSitePermission;

    let elementIds = [
      'ticketNumber',
      'statusesGroup',
      'summary',
      'priorities',
      'relationship',
      'creation',
      'updated',
      'closed',
      'category',
      'businessUnitName',
      'sites',
      'contact',
    ];
    let elementsNotLoaded = ['priorities', 'category', 'businessUnitName', 'sites'];
    // Remove company sites filters if the user doesn't have permissions to view them
    if (!this.canSeeSitesFilter) {
      this.filtersConfigMap.delete('sites');
      elementIds = elementIds.filter((id) => id !== 'sites');
      elementsNotLoaded = elementsNotLoaded.filter((id) => id !== 'sites');
    }

    // Remove business unit filters if the feature is not enabled
    if (!this.isBusinessUnitFlagEnabled || (!viewAllPermission && !viewBusinessUnitPermission)) {
      this.filtersConfigMap.delete('businessUnitName');
      elementIds = elementIds.filter((id) => id !== 'businessUnitName');
      elementsNotLoaded = elementsNotLoaded.filter((id) => id !== 'businessUnitName');
    }

    this.filtersLayoutConfig = new FiltersPanelLayoutConfig({
      elementIds,
      elementsWithoutCollapsible: ['ticketNumber', 'statusesGroup'],
      title: 'Ticket Filters',
      elementsNotLoaded,
    });
  }

  ngOnInit(): void {
    const boards$ = this.managedServiceRepository.getMspAccountBoards();
    const params$ = this.route.queryParams;

    const combined = combineLatest([boards$, params$]).subscribe(([boards, params]) => {
      // Boards are guaranteed to be loaded before proceeding
      this.mspBoards = boards;
      this.categoryItems = this.mspBoards.reduce((acc, board) => {
        if (!board.category) return acc;
        const category = acc.find((item) => item.id === board.category.toLowerCase());
        if (category) {
          category.children.push({
            id: String(board.id),
            displayName: board.name,
            children: [],
          });
        } else {
          acc.push({
            id: board.category.toLowerCase(),
            displayName: board.category,
            children: [
              {
                id: String(board.id),
                displayName: board.name,
                children: [],
              },
            ],
          });
        }
        return acc;
      }, []);

      this.canSeeCategoryFilter = this.mspBoards.length >= 2;
      if (!this.canSeeCategoryFilter) {
        this.filtersConfigMap.delete('category');
        const idIndex = this.filtersLayoutConfig.elementIds.indexOf('category');
        if (idIndex !== -1) {
          this.filtersLayoutConfig.elementIds.splice(idIndex, 1);
        }
        const notLoadedIndex = this.filtersLayoutConfig.elementsNotLoaded.indexOf('category');
        if (notLoadedIndex !== -1) {
          this.filtersLayoutConfig.elementsNotLoaded.splice(notLoadedIndex, 1);
        }
      }

      // Now categoryItems is always populated when this runs
      const hasFilters = Object.keys(this.managedServicesSharedDataService.getFilters()).length > 0;
      if (params['refresh']) {
        if (hasFilters) {
          this.setFiltersFromSharedService();
        } else {
          this.fetchTickets(true);
        }
      } else {
        this.fetchTickets();
      }
    });

    this.subscriptions.add(combined);

    this.subscriptions.add(
      this.filtersForm
        .get('businessUnitName')
        .valueChanges.pipe(
          map(() => this.getSelectedBusinessUnitNames().map(normalizeBusinessUnitName).sort().join('|')),
          distinctUntilChanged(),
        )
        .subscribe(() => this.rebuildSitesConfig()),
    );

    this.store.currentPageChanges.subscribe((currentPageData) => {
      this.dataSource = currentPageData.data ?? [];
      this.numberOfTickets = currentPageData.count;
    });
  }

  /** Called for event on the MatSort */
  onSortEvent(sortEvent: Sort) {
    const direction = sortEvent.direction === 'asc' ? 'ASC' : 'DESC';
    const sortColumn = mspTicketsSortColumns[sortEvent.active];

    // Do nothing if there are no direction or sortColumn changes
    if (direction === this.sortDirection && sortColumn === this.sortColumn) {
      return;
    }

    this.sortDirection = direction;
    this.sortColumn = sortColumn;
    this.pageIndex = 0;
    this.fetchTickets();
  }

  /** Called when there is an event on the paginator */
  onPageChange(pageEvent: PageEvent) {
    this.pageIndex = pageEvent.pageIndex;
    this.fetchTickets();
  }

  fetchTickets(shouldRefresh: boolean = false) {
    if (shouldRefresh) {
      this.store.clearCache();
    }

    this.store.requestPage(this.buildRequestData());
    this.setFormControlsConfigMinDates();
    this.setFormControlPriorities();
    if (this.canSeeCategoryFilter) {
      this.setFormControlCategories();
    }
    if (this.canSeeSitesFilter) {
      this.setFormCompanySites();
    }
    if (this.isBusinessUnitFlagEnabled) {
      this.setFormControlBusinessUnits();
    }
  }

  /** Removes the given [id] from the [elementsNotLoaded] array */
  removeIdFromNotLoadedElements(id: string) {
    this.filtersLayoutConfig.elementsNotLoaded.splice(this.filtersLayoutConfig.elementsNotLoaded.indexOf(id), 1);
  }

  setFormControlsConfigMinDates() {
    const formCreationDate = this.filtersConfigMap.get('creation') as DatetimeRangeQuickPickerControlConfig;

    const formUpdatedDate = this.filtersConfigMap.get('updated') as DatetimeRangeQuickPickerControlConfig;

    const formClosedDate = this.filtersConfigMap.get('closed') as DatetimeRangeQuickPickerControlConfig;

    formCreationDate.maxDate = this.today;
    formCreationDate.endDateTime = this.today.toISOString();

    formUpdatedDate.maxDate = this.today;
    formUpdatedDate.endDateTime = this.today.toISOString();

    formClosedDate.maxDate = this.today;
    formClosedDate.endDateTime = this.today.toISOString();
  }

  setFormControlPriorities() {
    this.managedServiceRepository.getMspPriorities().subscribe((priorities) => {
      const formPriorities = this.filtersConfigMap.get('priorities') as CheckboxTreeControlConfig;

      this.filtersConfigMap.set(
        'priorities',
        new CheckboxTreeControlConfig({
          ...formPriorities,
          items: priorities
            .map((priority) => ({
              id: String(priority.id),
              displayName: priority.name,
              children: [],
            }))
            .sort((a, b) => a.displayName.localeCompare(b.displayName)),
        }),
      );

      this.removeIdFromNotLoadedElements('priorities');
    });
  }

  setFormControlBusinessUnits() {
    this.managedServiceRepository.getBusinessUnits().subscribe((businessUnits) => {
      const formBusinessUnits = this.filtersConfigMap.get('businessUnitName') as CheckboxTreeControlConfig;

      const defaultBuTarget = normalizeBusinessUnitName(this.userDataService.user?.cwBusinessUnitName);
      const matchedDefaultBu =
        defaultBuTarget === '' ? null : businessUnits.find((bu) => normalizeBusinessUnitName(bu) === defaultBuTarget);

      const items = businessUnits.map((bu) => ({
        id: bu,
        displayName: bu === matchedDefaultBu ? `${bu} (Default)` : bu,
        children: [],
      }));
      if (matchedDefaultBu) {
        const idx = items.findIndex((i) => i.id === matchedDefaultBu);
        if (idx > 0) {
          const [defaultItem] = items.splice(idx, 1);
          items.unshift(defaultItem);
        }
      }

      this.filtersConfigMap.set(
        'businessUnitName',
        new CheckboxTreeControlConfig({
          ...formBusinessUnits,
          items,
        }),
      );

      this.removeIdFromNotLoadedElements('businessUnitName');
    });
  }

  setFormControlCategories() {
    const formCategories = this.filtersConfigMap.get('category') as CheckboxTreeControlConfig;

    this.filtersConfigMap.set(
      'category',
      new CheckboxTreeControlConfig({
        ...formCategories,
        items: this.categoryItems,
      }),
    );

    this.removeIdFromNotLoadedElements('category');
  }

  /** Updates the values map and fetches the tickets */
  updateValuesMap(valuesMap: ITicketFiltersForm) {
    this.valuesMap = valuesMap;
  }

  applyFilters() {
    this.pageIndex = 0;
    this.fetchTickets(true);
  }

  clearFilters() {
    this.pageIndex = 0;
    this.filtersForm.reset();
    this.valuesMap = this.filtersForm.value;
    this.fetchTickets(true);
  }

  /** Builds the request data or params for the API */
  private buildRequestData(): PagesStoreRequestData {
    const payload = {
      module: 'managed-services',
      pageSize: this.pageSize,
      pageIndex: this.pageIndex,
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection === 'ASC' ? 'ASC' : 'DESC',
    } as const;

    if (this.valuesMap.ticketNumber) {
      payload['id'] = this.valuesMap.ticketNumber;
    }

    if (this.valuesMap.statusesGroup?.length > 0 && this.valuesMap.statusesGroup?.length < 3) {
      payload['statusesGroup'] = this.valuesMap.statusesGroup.join(';');
    }

    if (this.valuesMap.summary) {
      payload['summary'] = this.valuesMap.summary;
    }

    if (this.valuesMap.priorities?.['Priorities']?.values?.length) {
      payload['priorityIds'] = this.valuesMap.priorities['Priorities'].values.join(';');
    }

    if (this.valuesMap.creation?.start) {
      payload['createdAfter'] = this.valuesMap.creation.start.toISOString();
    }

    if (this.valuesMap.creation?.end) {
      payload['createdBefore'] = this.valuesMap.creation.end.toISOString();
    }

    if (this.valuesMap.updated?.start) {
      payload['updatedAfter'] = this.valuesMap.updated.start.toISOString();
    }

    if (this.valuesMap.updated?.end) {
      payload['updatedBefore'] = this.valuesMap.updated.end.toISOString();
    }

    if (this.valuesMap.category?.['default']?.values?.length > 0) {
      payload['boardIds'] = this.valuesMap.category['default'].values.join(';');
    } else {
      payload['boardIds'] = this.mspBoards.map((board) => board.id).join(';');
    }

    if (this.valuesMap.closed?.start) {
      payload['closedAfter'] = this.valuesMap.closed.start.toISOString();
    }

    if (this.valuesMap.closed?.end) {
      payload['closedBefore'] = this.valuesMap.closed.end.toISOString();
    }

    if (this.valuesMap.contact) {
      payload['contact'] = this.valuesMap.contact;
    }

    if (this.valuesMap.relationship?.length === 1) {
      payload['relationship'] = this.valuesMap.relationship[0];
    }

    if (this.valuesMap.sites?.['Sites']?.values?.length) {
      payload['siteIds'] = this.valuesMap.sites['Sites'].values.join(';');
    }

    if (this.isBusinessUnitFlagEnabled && this.valuesMap.businessUnitName?.['Business Units']?.values?.length) {
      payload['businessUnitNames'] = this.valuesMap.businessUnitName['Business Units'].values.join(';');
    }

    return payload;
  }

  private mapHeaders(rawResponse: PagesStoreResponse<any>): PagesStoreResponse<any> {
    return {
      data: rawResponse.data,
      count: rawResponse.count,
    };
  }

  private mapPropertiesFn(object: ApiPagesStoreRequestData): {
    [key: string]: any;
  } {
    const $limit = 10;
    const $page = object.pageIndex + 1;
    const { sortColumn, sortDirection } = object;
    const $orderBy = `${sortColumn} ${sortDirection}`;

    //delete the field with key equals to pageSize, pageIndex, sortColumn, sortDirection, pageOffset
    const newRequestData = { ...object };

    delete newRequestData.pageSize;
    delete newRequestData.pageIndex;
    delete newRequestData.sortColumn;
    delete newRequestData.sortDirection;
    delete newRequestData.pageOffset;

    return {
      ...newRequestData,
      $limit,
      $page,
      $orderBy,
    };
  }

  private setFiltersFromSharedService() {
    this.filtersForm.reset();
    const filters = this.managedServicesSharedDataService.getFilters();

    if (filters['statusesGroup']?.length > 0) {
      this.filtersForm.patchValue({ statusesGroup: [filters['statusesGroup'][0]] });
    }

    if (Object.keys(filters).includes('boardNames')) {
      const boardIds = this.mspBoards
        .filter((board) => filters['boardNames'].includes(board.name))
        .map((board) => String(board.id));

      const boardCategory = filters['boardNames'][0] === 'Help Desk' ? 'request' : 'incident';

      this.filtersForm.patchValue({
        category: {
          Category: {
            labels: [boardCategory],
            parentsNotSelected: [boardCategory],
            values: [boardCategory],
          },
          default: {
            labels: filters['boardNames'],
            values: boardIds,
          },
        },
      });
    }

    if (Object.keys(filters).includes('priorityIds')) {
      this.filtersForm.patchValue({
        priorities: {
          Priorities: {
            values: [...filters['priorityIds'].map((id: number) => id.toString())],
          },
        },
      });
    }

    if (Object.keys(filters).includes('summary')) {
      this.filtersForm.patchValue({ summary: filters['summary'] });
    }

    if (Object.keys(filters).includes('closedAfter') && Object.keys(filters).includes('closedBefore')) {
      this.filtersForm.patchValue({
        closed: {
          start: new Date(filters['closedAfter']),
          end: new Date(filters['closedBefore']),
        },
      });
    }

    if (Object.keys(filters).includes('createdAfter') && Object.keys(filters).includes('createdBefore')) {
      this.filtersForm.patchValue({
        creation: {
          start: new Date(filters['createdAfter']),
          end: new Date(filters['createdBefore']),
        },
      });
    }

    if (Object.keys(filters).includes('relationship')) {
      this.filtersForm.patchValue({ relationship: [filters['relationship']] });
    }

    this.valuesMap = this.filtersForm.value;
    this.fetchTickets();
  }

  setFormCompanySites() {
    this.managedServiceRepository.getCompanySites().subscribe((sites) => {
      this.companySites = sites;
      this.rebuildSitesConfig();
      this.removeIdFromNotLoadedElements('sites');
    });
  }

  private rebuildSitesConfig(): void {
    const formSites = this.filtersConfigMap.get('sites') as CheckboxTreeControlConfig | undefined;
    if (!formSites || this.companySites === null) return;

    const selectedBuNames = this.getSelectedBusinessUnitNames();
    const defaultSite = this.getDefaultSite();

    let visibleSites: { id: number; name: string; businessUnitName: string | null }[];
    let emptyMessage = '';

    if (selectedBuNames.length === 0) {
      visibleSites = this.companySites;
    } else {
      const normalized = new Set(selectedBuNames.map(normalizeBusinessUnitName));
      visibleSites = this.companySites.filter((site) =>
        normalized.has(normalizeBusinessUnitName(site.businessUnitName)),
      );
      if (visibleSites.length === 0) {
        emptyMessage = 'No sites available for the selected Business Unit.';
      }
    }

    const defaultSiteIsVisible = defaultSite !== null && visibleSites.some((s) => s.id === defaultSite.id);

    const otherItems = visibleSites
      .filter((site) => !defaultSiteIsVisible || site.id !== defaultSite.id)
      .map((site) => ({ id: String(site.id), displayName: site.name, children: [] }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));

    const defaultItem = defaultSiteIsVisible
      ? [{ id: String(defaultSite.id), displayName: `${defaultSite.name} (Default)`, children: [] }]
      : [];

    this.filtersConfigMap.set(
      'sites',
      new CheckboxTreeControlConfig({
        ...formSites,
        items: [...defaultItem, ...otherItems],
        emptyMessage,
      }),
    );

    this.pruneSelectedSitesNotIn(visibleSites);
  }

  /**
   * Returns the user's default site (matched from `cwSiteName` against the
   * loaded company sites list), or null if the user has no default or the
   * name doesn't match any known site.
   */
  private getDefaultSite(): { id: number; name: string; businessUnitName: string | null } | null {
    const cwSiteName = this.userDataService.user?.cwSiteName;
    if (!cwSiteName || this.companySites === null) return null;
    const target = cwSiteName.trim().toLowerCase();
    return this.companySites.find((site) => site.name.trim().toLowerCase() === target) ?? null;
  }

  private getSelectedBusinessUnitNames(): string[] {
    const buValue = this.filtersForm.get('businessUnitName')?.value as Record<
      string,
      ICheckboxTreeSelectedItems
    > | null;
    return buValue?.['Business Units']?.values ?? [];
  }

  /** Removes any currently-selected site IDs that are not in [visibleSites]. */
  private pruneSelectedSitesNotIn(visibleSites: { id: number }[]): void {
    const sitesControl = this.filtersForm.get('sites');
    const currentValue = sitesControl?.value as Record<string, ICheckboxTreeSelectedItems> | null;
    const currentSelected = currentValue?.['Sites'];
    if (!currentSelected?.values?.length) return;

    const visibleIds = new Set(visibleSites.map((s) => String(s.id)));
    const keptIndices = currentSelected.values.map((id, i) => (visibleIds.has(id) ? i : -1)).filter((i) => i !== -1);

    if (keptIndices.length === currentSelected.values.length) return;

    if (keptIndices.length === 0) {
      sitesControl.setValue(null);
      return;
    }

    sitesControl.setValue({
      Sites: {
        ...currentSelected,
        values: keptIndices.map((i) => currentSelected.values[i]),
        labels: currentSelected.labels ? keptIndices.map((i) => currentSelected.labels[i]) : undefined,
      },
    });
  }
}
